<template>
    <v-form ref="form" lazy-validation v-if="value">
        <v-card flat color="transparent">
            <v-card-text class="py-0">
                <v-combobox
                    ref="couponInput"
                    v-model="couponCodes"
                    chips
                    deletable-chips
                    multiple
                    append-icon=""
                    class="pa-0 pt-2"
                    :error-messages="$language.translate(error)"
                    :label="$language.translate('PREPAID_COUPON_CODE')"
                >
                    <template v-slot:selection="{ item, parent, selected }">
                        <v-chip
                            color="primary"
                            close
                            text-color="white"
                            @input="!$event ? (couponCodes = couponCodes.filter(c => c !== item)) : null"
                        >
                            {{ item }}
                        </v-chip>
                    </template>
                </v-combobox>
                <v-btn block color="default" @click="submitInput" :loading="loading" class="ma-0 mb-3">
                    {{ 'APPLY_PREPAID_COUPON' | translation }}
                </v-btn>
                <v-text-field
                    v-if="totalAmount"
                    :label="$language.translate('PREPAID_COUPON_AMOUNT')"
                    disabled
                    hide-details
                    :value="$root.$options.filters.currency(totalAmount > basketTotal ? basketTotal : totalAmount)"
                />
            </v-card-text>
            <v-card-text v-if="totalAmount && !showFinishOrderBtn && !tooMuchCoupons" v-text="amountLeftToPay" />
            <v-card-text class="red--text" v-if="tooMuchCoupons">{{ 'TOO_MUCH_COUPONS' | translation }}</v-card-text>
            <v-card-actions>
                <v-btn color="primary" @click="finishOrder" :loading="loading" v-if="showFinishOrderBtn">
                    {{ 'COUPON_ORDER_FINISH' | translation }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';
import gql from 'graphql-tag';

export default {
    props: {
        value: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        const requiredMessage = this.$language.translate('Required');
        return {
            rules: {
                required: value => !!value || requiredMessage,
            },
            loading: false,
            error: '',
        };
    },
    mounted() {
        if (this.value) {
            this.reapplyCoupons();
        }
    },
    computed: {
        ...mapState('Coupon', ['paidCoupons']),
        ...mapGetters('Coupon', ['paidCouponsAmount']),
        coupons: {
            get() {
                return this.paidCoupons;
            },
        },
        tooMuchCoupons: {
            get() {
                const couponValues = Object.values(this.paidCoupons);
                return couponValues.slice(0, couponValues.length - 1).reduce((a, b) => a + b, 0) >= this.basketTotal;
            },
        },
        showFinishOrderBtn: {
            get() {
                const couponValues = Object.values(this.paidCoupons);
                return !this.tooMuchCoupons && couponValues.reduce((a, b) => a + b, 0) >= this.basketTotal;
            },
        },
        couponCodes: {
            get() {
                return Object.keys(this.paidCoupons);
            },
            set(values) {
                this.error = '';
                values.map(code => {
                    if (!this.paidCoupons[code]) {
                        this.couponApply(code);
                    }
                });
                Object.keys(this.paidCoupons).map(code => {
                    if (!values.includes(code)) {
                        this.removeCoupon(code);
                    }
                });
            },
        },
        totalAmount: {
            get() {
                return this.paidCouponsAmount;
            },
        },
        basketTotal() {
            return this.$basket.getBasketTotal();
        },
        amountLeftToPay() {
            const message = this.$language.translate('PAID_COUPON_AMOUNT_LEFT_TO_PAY %value%');
            return message.replace(
                '%value%',
                this.$root.$options.filters.currency(this.basketTotal - this.totalAmount),
            );
        },
    },
    methods: {
        ...mapMutations('Coupon', ['addPaidCoupon', 'setPaidCoupons', 'removePaidCoupon']),
        ...mapMutations('Payment', ['setError']),
        ...mapActions('Coupon', ['getCouponData']),
        submitInput() {
            this.$refs.couponInput.$emit('change');
        },
        reapplyCoupons() {
            const couponValues = Object.keys(this.paidCoupons);
            if (couponValues.length) {
                couponValues.map(code => {
                    this.couponApply(code);
                });
            }
        },
        couponApply(code) {
            this.error = '';
            this.loading = true;
            this.getCouponData({ coupon: code, prepaid: true })
                .then(result => {
                    if (result && result.message) {
                        this.error = result.message;
                        this.removeCoupon(code);
                    } else {
                        this.addPaidCoupon({
                            code,
                            amount: result.amount,
                        });

                        this.$emit('paid-coupon-used', true);
                    }
                })
                .catch(e => {
                    this.removeCoupon(code);
                    if (e.graphQLErrors && e.graphQLErrors.length) {
                        const error = e.graphQLErrors[0];
                        if (error && error.message && error.message.message) {
                            this.error = this.$language.translate(e.graphQLErrors[0].message.message);
                        }
                    } else {
                        this.error = this.$language.translate('Coupon was not found.');
                    }
                })
                .then(() => {
                    this.setPaidCoupons(this.paidCoupons);
                    this.loading = false;
                });
        },
        removeCoupon(coupon) {
            this.removePaidCoupon({ coupon });
        },
        finishOrder() {
            this.loading = true;
            this.$order.getOrder().then(order => {
                if (order && order.id) {
                    this.$graphql
                        .mutate({
                            mutation: gql`
                                mutation paidCouponExecutePayment(
                                    $orderId: Int!
                                    $transactionId: String!
                                    $coupons: [String!]!
                                ) {
                                    paidCouponExecutePayment(
                                        orderId: $orderId
                                        transactionId: $transactionId
                                        coupons: $coupons
                                    ) {
                                        success
                                    }
                                }
                            `,
                            variables: {
                                orderId: parseInt(order.id),
                                transactionId: order.transaction_id,
                                coupons: Object.keys(this.paidCoupons),
                            },
                        })
                        .then(r => r.data)
                        .then(({ paidCouponExecutePayment: { success, message } }) => {
                            if (message || !success) {
                                this.setError(message || 'Something went wrong during order finish');
                            } else {
                                this.$emit('success', order);
                            }
                        })
                        .catch(error => {
                            this.setError(error.message ? error.message : error.toString());
                        })
                        .finally(() => {
                            this.loading = false;
                        });
                } else {
                    this.loading = false;

                    this.setError('Order not created, please try again later.');
                }
            });
        },
    },
    watch: {
        showFinishOrderBtn() {
            this.$emit('hide-payments', this.showFinishOrderBtn || this.tooMuchCoupons);
        },
        value(value) {
            if (value) {
                this.reapplyCoupons();
                if (this.showFinishOrderBtn || this.tooMuchCoupons) {
                    this.$emit('hide-payments', this.showFinishOrderBtn || this.tooMuchCoupons);
                }
            } else {
                this.$emit('hide-payments', false);
                this.$emit('paid-coupon-used', false);
            }
        },
    },
};
</script>
